import React, { useState, useMemo, useEffect } from 'react';
import { usePostJob } from '../../hooks/useJobs';
import { useJobRoles } from '../../hooks/useUserQueries';
import TimePicker from '../common/TimePicker';
import './PostJobModal.css';

const PostJobModal = ({ isOpen, onClose, onSuccess, initialDate = null }) => {
  // Fetch job roles from backend
  const { data: jobRolesData, isLoading: isLoadingJobRoles } = useJobRoles();
  
  // Filter and sort active job roles
  const jobRoles = useMemo(() => {
    if (!jobRolesData) return [];
    return jobRolesData
      .filter(role => role.is_active)
      .sort((a, b) => a.job_role_name.localeCompare(b.job_role_name));
  }, [jobRolesData]);

  // Get default date: use initialDate if provided, otherwise today's date
  const getDefaultDate = useMemo(() => {
    if (initialDate) {
      return initialDate;
    }
    const today = new Date();
    return today.toISOString().split('T')[0];
  }, [initialDate]);

  const [formData, setFormData] = useState({
    job_role: '',
    job_date: '',
    start_time: '09:00',
    end_time: '17:00',
    break_start_time: '',
    break_end_time: '',
  });

  // Update job_date when initialDate changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        job_date: getDefaultDate
      }));
    }
  }, [isOpen, getDefaultDate]);

  const [errors, setErrors] = useState({});
  const postJobMutation = usePostJob();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Get selected job role details
  const selectedJobRole = useMemo(() => {
    if (!formData.job_role || !jobRolesData) return null;
    return jobRolesData.find(role => role.job_role_key === formData.job_role || role.id === formData.job_role);
  }, [formData.job_role, jobRolesData]);

  const calculateHours = () => {
    if (!formData.start_time || !formData.end_time) return 0;
    
    const start = new Date(`2000-01-01T${formData.start_time}`);
    const end = new Date(`2000-01-01T${formData.end_time}`);
    
    if (end <= start) return 0;
    
    let totalMs = end - start;
    
    // Subtract break time if break times are provided
    if (formData.break_start_time && formData.break_end_time) {
      const breakStart = new Date(`2000-01-01T${formData.break_start_time}`);
      const breakEnd = new Date(`2000-01-01T${formData.break_end_time}`);
      
      if (breakEnd > breakStart) {
        const breakMs = breakEnd - breakStart;
        totalMs -= breakMs;
      }
    }
    
    const diffHours = totalMs / (1000 * 60 * 60);
    return Math.max(0, diffHours).toFixed(1);
  };

  const calculateTotalCost = () => {
    const hours = parseFloat(calculateHours());
    if (!hours || !selectedJobRole || !selectedJobRole.hourly_rate) return 0;
    
    const hourlyRate = parseFloat(selectedJobRole.hourly_rate) || 0;
    return (hours * hourlyRate).toFixed(2);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.job_role) {
      newErrors.job_role = 'Job role is required';
    }

    if (!formData.job_date) {
      newErrors.job_date = 'Job date is required';
    } else {
      const selectedDate = new Date(formData.job_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.job_date = 'Job date must be in the future';
      }
    }

    if (!formData.start_time) {
      newErrors.start_time = 'Start time is required';
    }

    if (!formData.end_time) {
      newErrors.end_time = 'End time is required';
    }

    if (formData.start_time && formData.end_time) {
      const start = new Date(`2000-01-01T${formData.start_time}`);
      const end = new Date(`2000-01-01T${formData.end_time}`);
      
      if (end <= start) {
        newErrors.end_time = 'End time must be after start time';
      }
    }

    // Validate break times if provided
    if (formData.break_start_time || formData.break_end_time) {
      if (!formData.break_start_time) {
        newErrors.break_start_time = 'Break start time is required if break end time is provided';
      }
      if (!formData.break_end_time) {
        newErrors.break_end_time = 'Break end time is required if break start time is provided';
      }
      
      if (formData.break_start_time && formData.break_end_time) {
        const breakStart = new Date(`2000-01-01T${formData.break_start_time}`);
        const breakEnd = new Date(`2000-01-01T${formData.break_end_time}`);
        const start = new Date(`2000-01-01T${formData.start_time}`);
        const end = new Date(`2000-01-01T${formData.end_time}`);
        
        if (breakEnd <= breakStart) {
          newErrors.break_end_time = 'Break end time must be after break start time';
        }
        
        if (breakStart < start || breakEnd > end) {
          newErrors.break_start_time = 'Break time must be within work hours';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // Get job role ID from selected role
      const jobRoleId = selectedJobRole?.id || selectedJobRole?.job_role_id;
      if (!jobRoleId) {
        setErrors({ job_role: 'Please select a valid job role' });
        return;
      }
      
      const jobData = {
        job_role_id: jobRoleId, // Backend expects job_role_id
        job_date: formData.job_date,
        start_time: formData.start_time,
        end_time: formData.end_time,
        break_start_time: formData.break_start_time || null,
        break_end_time: formData.break_end_time || null,
        number_of_hours: parseFloat(calculateHours()),
      };
      
      await postJobMutation.mutateAsync(jobData);
      
      // Reset form
      setFormData({
        job_role: '',
        job_date: '',
        start_time: '09:00',
        end_time: '17:00',
        break_start_time: '',
        break_end_time: '',
      });
      setErrors({});
      
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (error) {
      // Error is handled by the hook
      console.error('Error posting job:', error);
    }
  };

  if (!isOpen) return null;

  const totalHours = calculateHours();
  const totalCost = calculateTotalCost();

  return (
    <div className="post-job-modal-overlay" onClick={onClose}>
      <div className="post-job-modal" onClick={(e) => e.stopPropagation()}>
        <div className="post-job-modal__header">
          <h2 className="post-job-modal__title">Post a New Job</h2>
          <button 
            className="post-job-modal__close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="post-job-modal__form">
          <div className="post-job-modal__field">
            <label htmlFor="job_role" className="post-job-modal__label">
              Job Role <span className="required">*</span>
            </label>
            <select
              id="job_role"
              name="job_role"
              value={formData.job_role}
              onChange={handleChange}
              className={`post-job-modal__select ${errors.job_role ? 'post-job-modal__select--error' : ''}`}
              required
              disabled={isLoadingJobRoles}
            >
              <option value="">Select job role</option>
              {jobRoles.map(role => (
                <option key={role.job_role_key || role.id} value={role.job_role_key || role.id}>
                  {role.job_role_name}
                </option>
              ))}
            </select>
            {errors.job_role && (
              <span className="post-job-modal__error">{errors.job_role}</span>
            )}
          </div>

          <div className="post-job-modal__field">
            <label htmlFor="job_date" className="post-job-modal__label">
              Job Date <span className="required">*</span>
            </label>
            <input
              type="date"
              id="job_date"
              name="job_date"
              value={formData.job_date}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
              className={`post-job-modal__input ${errors.job_date ? 'post-job-modal__input--error' : ''}`}
              required
            />
            {errors.job_date && (
              <span className="post-job-modal__error">{errors.job_date}</span>
            )}
          </div>

          <div className="post-job-modal__row">
            <div className="post-job-modal__field">
              <label htmlFor="start_time" className="post-job-modal__label">
                Start Time <span className="required">*</span>
              </label>
              <TimePicker
                id="start_time"
                name="start_time"
                value={formData.start_time}
                onChange={handleChange}
                error={errors.start_time}
                required
                placeholder="Select start time"
              />
            </div>

            <div className="post-job-modal__field">
              <label htmlFor="end_time" className="post-job-modal__label">
                End Time <span className="required">*</span>
              </label>
              <TimePicker
                id="end_time"
                name="end_time"
                value={formData.end_time}
                onChange={handleChange}
                error={errors.end_time}
                required
                placeholder="Select end time"
              />
            </div>
          </div>

          <div className="post-job-modal__row">
            <div className="post-job-modal__field">
              <label htmlFor="break_start_time" className="post-job-modal__label">
                Break Start Time
              </label>
              <TimePicker
                id="break_start_time"
                name="break_start_time"
                value={formData.break_start_time}
                onChange={handleChange}
                error={errors.break_start_time}
                placeholder="Select break start time"
              />
            </div>

            <div className="post-job-modal__field">
              <label htmlFor="break_end_time" className="post-job-modal__label">
                Break End Time
              </label>
              <TimePicker
                id="break_end_time"
                name="break_end_time"
                value={formData.break_end_time}
                onChange={handleChange}
                error={errors.break_end_time}
                placeholder="Select break end time"
              />
            </div>
          </div>

          {totalHours > 0 && (
            <div className="post-job-modal__hours-info">
              <div style={{ marginBottom: '0.5rem' }}>
                <span>Total Hours: <strong>{totalHours} hours</strong></span>
              </div>
              {selectedJobRole && selectedJobRole.hourly_rate && (
                <div>
                  <span>Total Cost: <strong>£{totalCost}</strong></span>
                  <small style={{ display: 'block', marginTop: '0.25rem', color: '#6b7280' }}>
                    (Rate: £{parseFloat(selectedJobRole.hourly_rate).toFixed(2)}/hour)
                  </small>
                </div>
              )}
            </div>
          )}

          <div className="post-job-modal__actions">
            <button
              type="button"
              className="post-job-modal__button post-job-modal__button--cancel"
              onClick={onClose}
              disabled={postJobMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="post-job-modal__button post-job-modal__button--submit"
              disabled={postJobMutation.isPending}
            >
              {postJobMutation.isPending ? 'Posting...' : 'Post Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostJobModal;











